import * as z from 'zod';

export const GetBalanceRequest = /*#__PURE__*/ z.object({
  poolId: z
    .string()
    .describe(
      'ID of the pool associated with the balance to retrieve. This is also the ID of the balance.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetBalanceResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe("Balance ID. This is the same as the associated pool's ID.")
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the balance is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the balance.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the balance was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the balance was updated.')
    .optional()
    .nullable(),
  beneficiary: z
    .intersection(
      z.object({}),
      z.xor([
        z.object({
          anonymousVisitorId: z.never().optional(),
          memberId: z.never().optional(),
          wixUserId: z.never().optional(),
        }),
        z.object({
          memberId: z.never().optional(),
          wixUserId: z.never().optional(),
          anonymousVisitorId: z
            .string()
            .describe("ID of a site visitor that hasn't logged in to the site.")
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          anonymousVisitorId: z.never().optional(),
          wixUserId: z.never().optional(),
          memberId: z
            .string()
            .describe('ID of a site member.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
        z.object({
          anonymousVisitorId: z.never().optional(),
          memberId: z.never().optional(),
          wixUserId: z
            .string()
            .describe('ID of a Wix user.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
        }),
      ])
    )
    .describe('Beneficiary of the associated pool.')
    .optional(),
  amount: z
    .object({
      available: z.string().describe('number of available credits.').optional(),
      reserved: z.string().describe('number of reserved credits.').optional(),
    })
    .describe('Available credits.')
    .optional(),
  poolInfo: z
    .object({
      _id: z
        .string()
        .describe('Pool ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      poolDefinitionId: z
        .string()
        .describe('ID of the pool definition the pool was created from.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programDefinitionId: z
        .string()
        .describe('ID of the program definition the pool was created from.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('ID of the program that contains the pool.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      creditAmount: z
        .string()
        .describe('Available credits.')
        .optional()
        .nullable(),
      namespace: z
        .string()
        .describe(
          "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
        )
        .min(1)
        .max(20)
        .optional()
        .nullable(),
      externalProgramDefinitionId: z
        .string()
        .describe(
          'External ID of the program definition the pool was created from.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalProgramId: z
        .string()
        .describe('External ID of the program that contains the pool.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      creditRolloverConfiguration: z
        .object({
          enabled: z
            .boolean()
            .describe(
              'Whether unused credits roll over to a new cycle when a program renews.'
            )
            .optional()
            .nullable(),
          balanceCap: z
            .string()
            .describe(
              'Maximum number of credits that can roll over to the next cycle when a program renews.'
            )
            .optional()
            .nullable(),
        })
        .describe('Credit rollover configuration information for the pool.')
        .optional(),
      status: z
        .enum([
          'ACTIVE',
          'PAUSED',
          'ENDED',
          'PROVISIONING',
          'RENEWING',
          'PENDING',
        ])
        .describe('Pool status')
        .optional(),
    })
    .describe('Associated pool information.')
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the balance object.\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  lastTransactionId: z
    .string()
    .describe(
      'ID of the transaction associated with the most recent change to the balance.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const ListBalancesRequest = /*#__PURE__*/ z.object({
  options: z
    .object({
      poolIds: z.array(z.string()).min(0).max(100).optional(),
      cursorPaging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Maximum number of items to return.')
            .min(0)
            .max(100)
            .optional()
            .nullable(),
          cursor: z
            .string()
            .describe(
              "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Cursor paging.')
        .optional(),
    })
    .optional(),
});
export const ListBalancesResponse = /*#__PURE__*/ z.object({
  balances: z
    .array(
      z.object({
        _id: z
          .string()
          .describe("Balance ID. This is the same as the associated pool's ID.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the balance is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the balance.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the balance was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the balance was updated.')
          .optional()
          .nullable(),
        beneficiary: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    "ID of a site visitor that hasn't logged in to the site."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                memberId: z
                  .string()
                  .describe('ID of a site member.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe('Beneficiary of the associated pool.')
          .optional(),
        amount: z
          .object({
            available: z
              .string()
              .describe('number of available credits.')
              .optional(),
            reserved: z
              .string()
              .describe('number of reserved credits.')
              .optional(),
          })
          .describe('Available credits.')
          .optional(),
        poolInfo: z
          .object({
            _id: z
              .string()
              .describe('Pool ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            poolDefinitionId: z
              .string()
              .describe('ID of the pool definition the pool was created from.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            programDefinitionId: z
              .string()
              .describe(
                'ID of the program definition the pool was created from.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            programId: z
              .string()
              .describe('ID of the program that contains the pool.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            creditAmount: z
              .string()
              .describe('Available credits.')
              .optional()
              .nullable(),
            namespace: z
              .string()
              .describe(
                "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            externalProgramDefinitionId: z
              .string()
              .describe(
                'External ID of the program definition the pool was created from.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            externalProgramId: z
              .string()
              .describe('External ID of the program that contains the pool.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            creditRolloverConfiguration: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether unused credits roll over to a new cycle when a program renews.'
                  )
                  .optional()
                  .nullable(),
                balanceCap: z
                  .string()
                  .describe(
                    'Maximum number of credits that can roll over to the next cycle when a program renews.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Credit rollover configuration information for the pool.'
              )
              .optional(),
            status: z
              .enum([
                'ACTIVE',
                'PAUSED',
                'ENDED',
                'PROVISIONING',
                'RENEWING',
                'PENDING',
              ])
              .describe('Pool status')
              .optional(),
          })
          .describe('Associated pool information.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the balance object.\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        lastTransactionId: z
          .string()
          .describe(
            'ID of the transaction associated with the most recent change to the balance.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .max(100)
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for paginated results.')
    .optional(),
});
export const QueryBalancesRequest = /*#__PURE__*/ z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'beneficiary.memberId': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'beneficiary.wixUserId': z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                '_createdDate',
                'beneficiary.memberId',
                'beneficiary.wixUserId',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Filter, sort, and paging to apply to the query.'),
});
export const QueryBalancesResponse = /*#__PURE__*/ z.object({
  balances: z
    .array(
      z.object({
        _id: z
          .string()
          .describe("Balance ID. This is the same as the associated pool's ID.")
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the balance is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the balance.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the balance was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the balance was updated.')
          .optional()
          .nullable(),
        beneficiary: z
          .intersection(
            z.object({}),
            z.xor([
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
              }),
              z.object({
                memberId: z.never().optional(),
                wixUserId: z.never().optional(),
                anonymousVisitorId: z
                  .string()
                  .describe(
                    "ID of a site visitor that hasn't logged in to the site."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                wixUserId: z.never().optional(),
                memberId: z
                  .string()
                  .describe('ID of a site member.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
              z.object({
                anonymousVisitorId: z.never().optional(),
                memberId: z.never().optional(),
                wixUserId: z
                  .string()
                  .describe('ID of a Wix user.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  ),
              }),
            ])
          )
          .describe('Beneficiary of the associated pool.')
          .optional(),
        amount: z
          .object({
            available: z
              .string()
              .describe('number of available credits.')
              .optional(),
            reserved: z
              .string()
              .describe('number of reserved credits.')
              .optional(),
          })
          .describe('Available credits.')
          .optional(),
        poolInfo: z
          .object({
            _id: z
              .string()
              .describe('Pool ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            poolDefinitionId: z
              .string()
              .describe('ID of the pool definition the pool was created from.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            programDefinitionId: z
              .string()
              .describe(
                'ID of the program definition the pool was created from.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            programId: z
              .string()
              .describe('ID of the program that contains the pool.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            creditAmount: z
              .string()
              .describe('Available credits.')
              .optional()
              .nullable(),
            namespace: z
              .string()
              .describe(
                "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            externalProgramDefinitionId: z
              .string()
              .describe(
                'External ID of the program definition the pool was created from.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            externalProgramId: z
              .string()
              .describe('External ID of the program that contains the pool.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            creditRolloverConfiguration: z
              .object({
                enabled: z
                  .boolean()
                  .describe(
                    'Whether unused credits roll over to a new cycle when a program renews.'
                  )
                  .optional()
                  .nullable(),
                balanceCap: z
                  .string()
                  .describe(
                    'Maximum number of credits that can roll over to the next cycle when a program renews.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Credit rollover configuration information for the pool.'
              )
              .optional(),
            status: z
              .enum([
                'ACTIVE',
                'PAUSED',
                'ENDED',
                'PROVISIONING',
                'RENEWING',
                'PENDING',
              ])
              .describe('Pool status')
              .optional(),
          })
          .describe('Associated pool information.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the balance object.\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        lastTransactionId: z
          .string()
          .describe(
            'ID of the transaction associated with the most recent change to the balance.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const ChangeBalanceRequest = /*#__PURE__*/ z.object({
  poolId: z
    .string()
    .describe(
      'ID of the pool associated with the balance to change. This is also the ID of the balance.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  idempotencyKey: z
    .string()
    .describe(
      'Unique identifier, generated by the client.\nUsed to recognize repeated attempts to make the same request.'
    )
    .max(200),
  options: z.intersection(
    z.object({
      instructingParty: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              anonymousVisitorId: z
                .string()
                .describe(
                  "ID of a site visitor that hasn't logged in to the site."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              wixUserId: z.never().optional(),
              memberId: z
                .string()
                .describe('ID of a site member.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z
                .string()
                .describe('ID of a Wix user.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe('Identity changing the balance.')
        .optional(),
      type: z.enum(['UNKNOWN_OPERATION', 'ADJUST', 'SET']).optional(),
      transactionDetails: z
        .object({
          item: z
            .object({
              _id: z
                .string()
                .describe('Item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              externalId: z
                .string()
                .describe('Item external ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              category: z
                .string()
                .describe('Item category.')
                .max(20)
                .optional()
                .nullable(),
              itemSetId: z
                .string()
                .describe('Item set ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              displayName: z
                .string()
                .describe('Item name.')
                .max(400)
                .optional()
                .nullable(),
              providerAppId: z
                .string()
                .describe('Provider app id')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
            })
            .describe('Item associated with the transaction.')
            .optional(),
          itemCount: z
            .number()
            .int()
            .describe('Amount of items associated with the transaction.')
            .min(0)
            .optional()
            .nullable(),
          effectiveDate: z
            .date()
            .describe('Date and time the transaction was created.')
            .optional()
            .nullable(),
          reason: z
            .string()
            .describe('Reason for the transaction. For example, `Redemption`.')
            .max(256)
            .optional()
            .nullable(),
          benefitKey: z
            .string()
            .describe('Benefit key associated with the transaction.')
            .max(64)
            .optional()
            .nullable(),
        })
        .describe(
          'Details to send to the transaction created from this balance change.'
        )
        .optional(),
    }),
    z.xor([
      z.object({
        setOptions: z.never().optional(),
        adjustOptions: z
          .object({
            value: z
              .string()
              .describe(
                "Amount to change the balance's available credits by.\n- To increase the balance, specify a positive number.\n- To decrease the balance, specify a negative number."
              )
              .optional(),
            beneficiary: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    anonymousVisitorId: z
                      .string()
                      .describe(
                        "ID of a site visitor that hasn't logged in to the site."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe('ID of a site member.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z
                      .string()
                      .describe('ID of a Wix user.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe('Beneficiary of the associated pool.')
              .optional(),
          })
          .describe('Adjust the balance by a specific number of credits.'),
      }),
      z.object({
        adjustOptions: z.never().optional(),
        setOptions: z
          .object({
            value: z
              .string()
              .describe(
                "Amount to set the balance's available credits to.\n\nIf this is the same as the current value, no transaction is created."
              )
              .optional(),
            beneficiary: z
              .intersection(
                z.object({}),
                z.xor([
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                  }),
                  z.object({
                    memberId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    anonymousVisitorId: z
                      .string()
                      .describe(
                        "ID of a site visitor that hasn't logged in to the site."
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    wixUserId: z.never().optional(),
                    memberId: z
                      .string()
                      .describe('ID of a site member.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    anonymousVisitorId: z.never().optional(),
                    memberId: z.never().optional(),
                    wixUserId: z
                      .string()
                      .describe('ID of a Wix user.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe('Beneficiary of the associated pool.')
              .optional(),
          })
          .describe('Set the balance to a specific number of credits.'),
      }),
    ])
  ),
});
export const ChangeBalanceResponse = /*#__PURE__*/ z.object({
  balance: z
    .object({
      _id: z
        .string()
        .describe("Balance ID. This is the same as the associated pool's ID.")
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the balance is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the balance.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the balance was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the balance was updated.')
        .optional()
        .nullable(),
      beneficiary: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              anonymousVisitorId: z
                .string()
                .describe(
                  "ID of a site visitor that hasn't logged in to the site."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              wixUserId: z.never().optional(),
              memberId: z
                .string()
                .describe('ID of a site member.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z
                .string()
                .describe('ID of a Wix user.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe('Beneficiary of the associated pool.')
        .optional(),
      amount: z
        .object({
          available: z
            .string()
            .describe('number of available credits.')
            .optional(),
          reserved: z
            .string()
            .describe('number of reserved credits.')
            .optional(),
        })
        .describe('Available credits.')
        .optional(),
      poolInfo: z
        .object({
          _id: z
            .string()
            .describe('Pool ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          poolDefinitionId: z
            .string()
            .describe('ID of the pool definition the pool was created from.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          programDefinitionId: z
            .string()
            .describe('ID of the program definition the pool was created from.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          programId: z
            .string()
            .describe('ID of the program that contains the pool.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          creditAmount: z
            .string()
            .describe('Available credits.')
            .optional()
            .nullable(),
          namespace: z
            .string()
            .describe(
              "Namespace for your app or site's benefit programs. Namespaces allow you to distinguish between entities that you created and entities that other apps created."
            )
            .min(1)
            .max(20)
            .optional()
            .nullable(),
          externalProgramDefinitionId: z
            .string()
            .describe(
              'External ID of the program definition the pool was created from.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          externalProgramId: z
            .string()
            .describe('External ID of the program that contains the pool.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          creditRolloverConfiguration: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  'Whether unused credits roll over to a new cycle when a program renews.'
                )
                .optional()
                .nullable(),
              balanceCap: z
                .string()
                .describe(
                  'Maximum number of credits that can roll over to the next cycle when a program renews.'
                )
                .optional()
                .nullable(),
            })
            .describe('Credit rollover configuration information for the pool.')
            .optional(),
          status: z
            .enum([
              'ACTIVE',
              'PAUSED',
              'ENDED',
              'PROVISIONING',
              'RENEWING',
              'PENDING',
            ])
            .describe('Pool status')
            .optional(),
        })
        .describe('Associated pool information.')
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the balance object.\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      lastTransactionId: z
        .string()
        .describe(
          'ID of the transaction associated with the most recent change to the balance.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Updated balance.')
    .optional(),
  transactionId: z
    .string()
    .describe('ID of the transaction associated with this change in balance.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const RevertBalanceChangeRequest = /*#__PURE__*/ z.object({
  transactionId: z
    .string()
    .describe(
      'ID of the transaction associated with the balance change to revert.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  idempotencyKey: z
    .string()
    .describe(
      'Ignored value, kept for backward compatibility. Idempotency is now handled by the system.'
    )
    .max(200),
  options: z
    .object({
      instructingParty: z
        .intersection(
          z.object({}),
          z.xor([
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
            }),
            z.object({
              memberId: z.never().optional(),
              wixUserId: z.never().optional(),
              anonymousVisitorId: z
                .string()
                .describe(
                  "ID of a site visitor that hasn't logged in to the site."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              wixUserId: z.never().optional(),
              memberId: z
                .string()
                .describe('ID of a site member.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              anonymousVisitorId: z.never().optional(),
              memberId: z.never().optional(),
              wixUserId: z
                .string()
                .describe('ID of a Wix user.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe('Identity reverting the balance change.')
        .optional(),
    })
    .optional(),
});
export const RevertBalanceChangeResponse = /*#__PURE__*/ z.object({
  transactionId: z
    .string()
    .describe('ID of the transaction associated with the balance reversion.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
